import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import Breadcrumbs from "../../containers/Breadcrumbs";
import ReturnToLandingPageButton from "../../containers/ReturnToLandingPageButton";
import { appHeaderTitle, toTitleCase } from "../../common/services/Utilities";
import { KeyValue } from "../../interfaces";
import { formatId, ReleaseNotes } from "../../refdata/constants";
import BackButton from "../../containers/BackButton";
import { useNavigate, useLocation, useNavigationType } from "react-router-dom";
import clsx from "clsx";
import Glossary from "./Glossary";
import Disclaimers from "./Disclaimers";
import Tutorials from "./Tutorials/Tutorials";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import SODOverview from "./SOD/Overview";
import SODAmendments from "./SOD/DataAmendments";
import SODMSAInfo from "./SOD/MSAInformation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";

const PREFIX = "Help";

const classes = {
  listItem: `${PREFIX}-listItem`,
  MuiSvgIcon: `${PREFIX}-MuiSvgIcon`,
  IndentedList: `${PREFIX}-IndentedList`,
  HelpTopic: `${PREFIX}-HelpTopic`,
  HelpTopicNormalWeight: `${PREFIX}-HelpTopicNormalWeight`,
  IndentedIndented: `${PREFIX}-IndentedIndented`,
  ReleaseNotes: `${PREFIX}-ReleaseNotes`,
  clickableList: `${PREFIX}-clickableList`,
  clickableAccordian: `${PREFIX}-clickableAccordian`,
  dropdownItem: `${PREFIX}-dropdownItem`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.listItem}`]: {
    "margin": 0,
    "& h3": {
      paddingBottom: 0,
    },
  },

  [`& .${classes.MuiSvgIcon}`]: {
    color: theme.palette.primary.main,
    fontSize: 10,
  },

  [`& .${classes.IndentedList}`]: {
    color: theme.palette.primary.main,
    marginLeft: 40,
    lineHeight: "24px",
  },

  [`& .${classes.HelpTopic}`]: {
    marginLeft: 8,
    color: theme.palette.primary.main,
  },

  [`& .${classes.HelpTopicNormalWeight}`]: {
    marginLeft: 8,
    color: theme.palette.primary.main,
    fontWeight: "normal",
  },

  [`& .${classes.IndentedIndented}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.ReleaseNotes}`]: {
    marginLeft: 8,
    color: theme.palette.primary.main,
    paddingBottom: 0,
    lineHeight: "32px",
  },

  [`& .${classes.clickableList}`]: {
    "margin": "0px 0px .25rem 0px",
    "& h3": {
      cursor: "pointer",
      paddingBottom: ".5rem",
      paddingTop: ".5rem",
    },
    "&:hover": {
      background: theme.palette.secondary.light,
      cursor: "pointer",
    },
    "&.active": {
      background: theme.palette.secondary.main,
    },
    "&:focus": {
      outlineColor: theme.palette.secondary.main,
    },
  },

  [`& .${classes.clickableAccordian}`]: {
    "&:hover": {
      background: theme.palette.secondary.main,
    },
  },

  [`& .${classes.dropdownItem}`]: {
    paddingTop: "1rem",
    paddingLeft: "1rem",
  },
}));

const introParagraph = (
  <>
    {appHeaderTitle("help")}
    <Typography variant="body2" id="help-description">
      Use the Help Topics to locate the information you’re looking for. Provide{" "}
      <Link
        href={
          "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=Help"
        }
      >
        feedback or submit a question
      </Link>{" "}
      about this page.
    </Typography>
  </>
);

export default function Help() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const urlSearch = location.search ? location.search : "";

  const [helpTopic, setHelpTopic] = React.useState("");
  const [app, setApp] = React.useState("HomePage");
  const [sodExpanded, setSodExpanded] = React.useState(false);

  React.useEffect(() => {
    if (urlSearch.includes("summary-of-deposits-")) {
      setSodExpanded(true);
    }
  }, [urlSearch]);

  const handleClick = (e: any) => {
    e.preventDefault();
    if (e.currentTarget.title === "Summary of Deposits") {
      setHelpTopic("Overview");
    } else {
      const urlTopicStr: string = e.currentTarget.title.replace("and", "&");
      setHelpTopic(urlTopicStr);
    }

    if (window.location.href.split("?helpTopic=")[1] !== e.currentTarget.id) {
      const newUrl = e.currentTarget.id.replace("4-header", "overview");
      navigate(`/help?helpTopic=${newUrl}`);
    }
  };

  React.useEffect(() => {
    if (navigationType === "POP" && urlSearch.includes("API")) {
      setApp("API");
    }
  }, [navigate, navigationType, urlSearch]);
  let selectedHelpTopic = window.location.href.split("?helpTopic=")[1];
  selectedHelpTopic =
    selectedHelpTopic &&
    selectedHelpTopic
      .replace("summary-of-deposits-", "")
      .replace("and", "&")
      .split("-")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("-");

  React.useEffect(() => {
    if (navigationType === "POP" && selectedHelpTopic) {
      const helpTopic = selectedHelpTopic
        .replace("and", "&")
        .replace(/-/g, " ");
      setHelpTopic(helpTopic);
    }
  }, [navigationType, selectedHelpTopic]);
  React.useEffect(() => {
    if (!helpTopic && selectedHelpTopic) {
      const helpTopic =
        selectedHelpTopic === "msa-information"
          ? "MSA Information"
          : toTitleCase(
              selectedHelpTopic.replace("and", "&").replace(/-/g, " "),
            );
      setHelpTopic(helpTopic);
    }
    if (helpTopic === "" && !selectedHelpTopic) {
      setHelpTopic("Tutorials");
    }
  }, [helpTopic, selectedHelpTopic]);

  const HelpTopics = [
    // { title: "Data Availability", subTitle: [] },
    { title: "Tutorials", subTitle: [] },
    { title: "Glossary & Variable Definitions", subTitle: [] },
    { title: "Release Notes", subTitle: [] },
    { title: "Disclaimers & Methodologies", subTitle: [] },
    {
      title: "Summary of Deposits",
      subTitle: ["Overview", "Data Amendments", "MSA Information"],
    },
  ];

  const selectedHelpTopicTitle =
    helpTopic === "Overview" ||
    helpTopic === "Data Amendments" ||
    helpTopic === "MSA Information"
      ? `Summary of Deposits (SOD) - ${helpTopic}`
      : helpTopic;

  const topicTitle = (
    <Typography variant="h2" id={formatId(helpTopic)}>
      {selectedHelpTopicTitle}
    </Typography>
  );

  useInitialPageLoad("help");

  return (
    <Root>
      <title>Help Library</title>
      <Breadcrumbs currentPageBreadCrumbTitle="Help Library" />
      <CloudFoundryBanner />
      <ReturnToLandingPageButton />{" "}
      <BackButton app={app} buttonLabel="Back to Previous" variant="dark" />
      {introParagraph}
      <Grid container spacing={2}>
        <Grid size={3}>
          <Typography variant="h2" id="help_topics">
            Help Topics
          </Typography>
          <ul style={{ color: "#1A4480" }}>
            {HelpTopics.map((topic: KeyValue, i: number) => {
              const id = `${formatId(topic.title)}-${i}`;
              const isActive = helpTopic === topic.title;

              return topic.subTitle.length ? (
                <div key={i}>
                  <Divider
                    sx={{
                      borderBottomWidth: "1px",
                      borderColor: "#acc8df",
                      my: 2,
                    }}
                  />
                  <li key={id} className={classes.listItem}>
                    <Accordion
                      sx={{
                        boxShadow: "none",
                        borderBottom: "none",
                      }}
                      disableGutters
                      expanded={sodExpanded}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${id}-content`}
                        id={`${id}-header`}
                        sx={{ padding: 0, marginTop: -0.5 }}
                        onClick={(e) => {
                          handleClick(e);
                          setSodExpanded(!sodExpanded);
                        }}
                        title={topic.title}
                        className={clsx(classes.clickableAccordian)}
                      >
                        <Typography
                          variant="h3"
                          className={`${classes.HelpTopic} ${
                            isActive ? "active" : ""
                          }`}
                        >
                          {topic.title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        style={{
                          padding: 0,
                        }}
                      >
                        {topic.subTitle.map((subTitle: string, j: number) => (
                          <Box
                            key={`${id}-sub-${j}`}
                            onClick={handleClick}
                            onKeyDown={(e) =>
                              e.key === "Enter" ? handleClick(e) : undefined
                            }
                            id={`${formatId(topic.title)}-${formatId(
                              subTitle,
                            )}`}
                            tabIndex={0}
                            title={subTitle}
                            className={clsx(
                              classes.clickableList,
                              helpTopic === subTitle ? "active" : "",
                            )}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              height: "2rem",
                            }}
                          >
                            <Typography
                              variant="body2"
                              className={clsx(
                                classes.HelpTopic,
                                classes.dropdownItem,
                              )}
                            >
                              {subTitle}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </li>
                </div>
              ) : (
                <li
                  key={id}
                  onClick={handleClick}
                  onKeyDown={(e) =>
                    e.key === "Enter" ? handleClick(e) : undefined
                  }
                  id={`${formatId(topic.title)}`}
                  tabIndex={0}
                  title={topic.title}
                  className={clsx(
                    classes.clickableList,
                    isActive ? "active" : "",
                  )}
                >
                  <Typography variant="h3" className={classes.HelpTopic}>
                    {topic.title}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </Grid>
        <>
          {helpTopic === "Release Notes" && (
            <Grid
              size={9}
              sx={{
                padding: 2,
              }}
            >
              {topicTitle}
              {ReleaseNotes.map((topic: KeyValue, i: number) => (
                <div key={`${formatId(topic.heading)}-${i}`}>
                  <Typography
                    variant="h2"
                    className={classes.ReleaseNotes}
                    id={formatId(topic.heading)}
                  >
                    {topic.heading}
                  </Typography>
                  <ul className={classes.listItem}>
                    {topic.title.length
                      ? topic.title.map((element: KeyValue, i: number) => (
                          <li key={`${formatId(element.subHeading)}-${i}`}>
                            <ul style={{ padding: "0 0 1rem 0" }}>
                              <Typography
                                variant="h3"
                                className={
                                  element.subTitle.length
                                    ? classes.HelpTopic
                                    : classes.HelpTopicNormalWeight
                                }
                              >
                                {element.subHeading}
                              </Typography>

                              {element.subTitle[0]
                                ? element.subTitle.map(
                                    (subTitle: string, i: number) => (
                                      <li
                                        key={`${formatId(subTitle)}-${i}`}
                                        className={classes.IndentedList}
                                      >
                                        {subTitle}
                                        {element.additionalNotes &&
                                        element.additionalNotes.length &&
                                        element.additionalNotes.length >=
                                          i + 1 ? (
                                          <ul>
                                            {element.additionalNotes.map(
                                              (
                                                additionalNotes: string,
                                                i: number,
                                              ) => (
                                                <li
                                                  key={`${formatId(
                                                    additionalNotes,
                                                  )}-${i}`}
                                                >
                                                  {additionalNotes}
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        ) : (
                                          ""
                                        )}
                                      </li>
                                    ),
                                  )
                                : ""}
                            </ul>
                          </li>
                        ))
                      : ""}
                  </ul>
                </div>
              ))}
            </Grid>
          )}
          {helpTopic === "Glossary & Variable Definitions" && (
            <Glossary title={topicTitle} />
          )}
          {helpTopic === "Disclaimers & Methodologies" && (
            <Grid
              size={9}
              sx={{
                padding: 2,
              }}
            >
              <Disclaimers title={topicTitle} />
            </Grid>
          )}
          {helpTopic === "Tutorials" && (
            <Grid
              size={9}
              sx={{
                padding: 2,
              }}
            >
              <Tutorials title={topicTitle} />
            </Grid>
          )}
          {helpTopic === "Overview" && (
            <Grid
              size={9}
              sx={{
                padding: 2,
              }}
            >
              <SODOverview title={topicTitle} />
            </Grid>
          )}
          {helpTopic === "Data Amendments" && (
            <Grid
              size={9}
              sx={{
                padding: 2,
              }}
            >
              <SODAmendments title={topicTitle} />
            </Grid>
          )}
          {helpTopic === "MSA Information" && (
            <Grid
              size={9}
              sx={{
                padding: 2,
              }}
            >
              <SODMSAInfo title={topicTitle} />
            </Grid>
          )}
        </>
      </Grid>
    </Root>
  );
}
